import React, {useEffect, useMemo, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AppBar, Avatar, Box, Toolbar, Typography} from "@mui/material";
import {FiRepeat} from "react-icons/fi";
import {accountsApi, authStorage} from "../../services/api";
import useAuthRole from "../../hooks/useAuthRole";

const Logo = `${process.env.PUBLIC_URL}/img/SAM-logo.svg`;

const getDisplayName = (user = {}) => {
    const name = user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return name || user.email || "کاربر سامانه";
};

const getInitials = (user = {}) => {
    const displayName = getDisplayName(user);
    if (user.first_name || user.last_name) {
        return `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`;
    }
    return displayName.slice(0, 2);
};

const getRoleTitle = (role, isPlatformAdmin = false, isOrganizationManager = false) => {
    if (isPlatformAdmin) return "مدیر سامانه";
    if (role === "teacher" && isOrganizationManager) return "مدیر سازمان";
    if (role === "student") return "دانشجو";
    if (role === "teacher") return "استاد";
    return "کاربر سامانه آزمون";
};

const AppHeader = () => {
    const navigate = useNavigate();
    const {role, isPlatformAdmin, isOrganizationManager, canTeach, canStudy, switchRole} = useAuthRole();
    const [session, setSession] = useState(() => authStorage.get());

    useEffect(() => {
        const syncSession = () => setSession(authStorage.get());
        window.addEventListener("auth-change", syncSession);
        return () => window.removeEventListener("auth-change", syncSession);
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!authStorage.isAuthenticated() || session.user) return undefined;

        accountsApi.profile()
            .then(() => {
                if (!cancelled) setSession(authStorage.get());
            })
            .catch(() => {
                if (!cancelled) setSession(authStorage.get());
            });

        return () => {
            cancelled = true;
        };
    }, [session.user]);

    const user = useMemo(() => session.user || {}, [session.user]);
    const isAuthenticated = authStorage.isAuthenticated();
    const displayName = getDisplayName(user);
    const roleTitle = getRoleTitle(role, isPlatformAdmin, isOrganizationManager);
    const profileSubtitle = user.email || roleTitle;
    const initials = useMemo(() => getInitials(user), [user]);

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                bgcolor: "rgba(255,255,255,0.65)",
                backdropFilter: "blur(10px)",
                borderBottom: "1px solid rgba(2,132,199,0.15)",
                color: "var(--gray-900)",
            }}
        >
            <Toolbar sx={{minHeight: {xs: 64, sm: 72}, px: {xs: 1.5, sm: 3}, display: "flex", justifyContent: "space-between", gap: {xs: 1, sm: 2}}}>
                {/* راست: لوگو + عنوان */}
                <Box sx={{display: "flex", alignItems: "center", gap: {xs: 0.75, sm: 1.5}, minWidth: 0}}>
                    <Box
                        component="img"
                        src={Logo}
                        alt="لوگوی سام"
                        sx={
                            {
                                width: {xs: 40, sm: 48},
                                height: {xs: 40, sm: 48},
                                objectFit: "contain",
                            }
                        }
                    />
                    <Box sx={{minWidth: 0}}>
                        <Typography sx={{fontWeight: 900, lineHeight: 1.1}}>
                            سام
                        </Typography>
                        <Typography variant="caption" sx={{display: {xs: "none", sm: "block"}, color: "var(--gray-600)"}}>
                            سامانه آزمون مجازی
                        </Typography>
                    </Box>
                </Box>

                {/* چپ: دسترسی سریع به پروفایل */}
                <Box sx={{display: "flex", alignItems: "center", gap: 1, minWidth: 0}}>
                    {isAuthenticated && canTeach && canStudy && (
                        <Box
                            component="button"
                            type="button"
                            onClick={() => {
                                const nextRole = role === "teacher" ? "student" : "teacher";
                                if (switchRole(nextRole)) navigate(nextRole === "student" ? "/exampage" : "/dashboard");
                            }}
                            aria-label={`تغییر به نمای ${role === "teacher" ? "دانشجو" : "استاد"}`}
                            sx={{
                                minHeight: 38,
                                px: {xs: 1, sm: 1.5},
                                border: "1px solid rgba(2,132,199,0.2)",
                                borderRadius: "12px",
                                bgcolor: "var(--primary-50)",
                                color: "var(--primary-700)",
                                fontFamily: "inherit",
                                fontWeight: 800,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                whiteSpace: "nowrap",
                            }}
                        >
                            <FiRepeat/>
                            <Box component="span" sx={{display: {xs: "none", sm: "inline"}}}>
                                نمای {role === "teacher" ? "دانشجو" : "استاد"}
                            </Box>
                        </Box>
                    )}
                    {isAuthenticated && (
                        <Box
                            component={Link}
                            to="/profilepage?tab=profile"
                            aria-label={`مشاهده پروفایل ${displayName}`}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                                px: {xs: 0.5, sm: 1.25},
                                py: {xs: 0.5, sm: 0.75},
                                border: "1px solid rgba(15,23,42,0.08)",
                                borderRadius: "14px",
                                bgcolor: "rgba(255,255,255,0.86)",
                                color: "inherit",
                                textDecoration: "none",
                                boxShadow: "0 10px 28px rgba(15,23,42,0.06)",
                                transition: "0.2s ease",
                                "&:hover": {
                                    borderColor: "rgba(2,132,199,0.28)",
                                    bgcolor: "#fff",
                                },
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 38,
                                    height: 38,
                                    bgcolor: "var(--primary-600)",
                                    fontSize: 14,
                                    fontWeight: 800,
                                }}
                            >
                                {initials}
                            </Avatar>
                            <Box sx={{display: {xs: "none", sm: "block"}, minWidth: 0, textAlign: "right"}}>
                                <Typography sx={{fontWeight: 800, fontSize: 14, lineHeight: 1.35}} noWrap>
                                    {displayName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{display: "block", color: "var(--gray-500)", maxWidth: 190}}
                                    noWrap
                                >
                                    {profileSubtitle}
                                </Typography>
                            </Box>
                            {user.email && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: {xs: "none", md: "inline-flex"},
                                        px: 1,
                                        py: 0.35,
                                        borderRadius: "999px",
                                        bgcolor: "var(--primary-50)",
                                        color: "var(--primary-700)",
                                        fontWeight: 700,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {roleTitle}
                                </Typography>
                            )}
                        </Box>
                    )}

                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default AppHeader;
